import React from "react";

export interface ActiveHiringStageFilters {
  activeRoleFitFilters: Set<string>;
  activeKnockoutPassFilters: Set<string>;
  activeKnockoutFailFilters: Set<string>;
  activeKnockoutNoReplyFilters: Set<string>;
  activeSourceFilters: Set<string>;
  activeHasResumeFilter: boolean | null;
  activeCreatedViaFilters: Set<string>;
}

export interface AppliedHiringStageFilters {
  appliedRoleFitFilters: Set<string>;
  appliedKnockoutPassFilters: Set<string>;
  appliedKnockoutFailFilters: Set<string>;
  appliedKnockoutNoReplyFilters: Set<string>;
  appliedSourceFilters: Set<string>;
  appliedHasResumeFilter: boolean | null;
  appliedCreatedViaFilters: Set<string>;
}

const emptyActiveFilters = (): ActiveHiringStageFilters => ({
  activeRoleFitFilters: new Set<string>(),
  activeKnockoutPassFilters: new Set<string>(),
  activeKnockoutFailFilters: new Set<string>(),
  activeKnockoutNoReplyFilters: new Set<string>(),
  activeSourceFilters: new Set<string>(),
  activeHasResumeFilter: null,
  activeCreatedViaFilters: new Set<string>(),
});

const emptyAppliedFilters = (): AppliedHiringStageFilters => ({
  appliedRoleFitFilters: new Set<string>(),
  appliedKnockoutPassFilters: new Set<string>(),
  appliedKnockoutFailFilters: new Set<string>(),
  appliedKnockoutNoReplyFilters: new Set<string>(),
  appliedSourceFilters: new Set<string>(),
  appliedHasResumeFilter: null,
  appliedCreatedViaFilters: new Set<string>(),
});

const toAppliedFilters = (
  activeFilters: ActiveHiringStageFilters,
): AppliedHiringStageFilters => ({
  appliedRoleFitFilters: new Set(activeFilters.activeRoleFitFilters),
  appliedKnockoutPassFilters: new Set(activeFilters.activeKnockoutPassFilters),
  appliedKnockoutFailFilters: new Set(activeFilters.activeKnockoutFailFilters),
  appliedKnockoutNoReplyFilters: new Set(activeFilters.activeKnockoutNoReplyFilters),
  appliedSourceFilters: new Set(activeFilters.activeSourceFilters),
  appliedHasResumeFilter: activeFilters.activeHasResumeFilter,
  appliedCreatedViaFilters: new Set(activeFilters.activeCreatedViaFilters),
});

const copyActiveFilters = (
  activeFilters: ActiveHiringStageFilters,
): ActiveHiringStageFilters => ({
  activeRoleFitFilters: new Set(activeFilters.activeRoleFitFilters),
  activeKnockoutPassFilters: new Set(activeFilters.activeKnockoutPassFilters),
  activeKnockoutFailFilters: new Set(activeFilters.activeKnockoutFailFilters),
  activeKnockoutNoReplyFilters: new Set(activeFilters.activeKnockoutNoReplyFilters),
  activeSourceFilters: new Set(activeFilters.activeSourceFilters),
  activeHasResumeFilter: activeFilters.activeHasResumeFilter,
  activeCreatedViaFilters: new Set(activeFilters.activeCreatedViaFilters),
});

// The stage's filter selections, one Set per filter kind, held in two objects.
// `activeRoleFitFilters` holds band keys; each knockout Set holds one entry per
// added question filter, that filter's question ids joined by commas ("63", or
// "33,41" for a merged group) — strings rather than arrays because a Set compares
// arrays by reference. `activeSourceFilters` holds the served source values and
// `activeCreatedViaFilters` the served created-via option keys; `activeHasResumeFilter`
// is `true`, `false`, or `null` for unset.
//
// activeFilters = the live selection in the dropdown and the modal (controlled).
// appliedFilters = what the list query actually reads, so toggling a control does
// not refetch. Applied is updated by the dropdown's Done, the modal's Done, and Clear.
export const useHiringStageFilters = (stageId) => {
  const [activeFilters, setActiveFilters] =
    React.useState<ActiveHiringStageFilters>(emptyActiveFilters);
  const [appliedFilters, setAppliedFilters] =
    React.useState<AppliedHiringStageFilters>(emptyAppliedFilters);

  React.useEffect(() => {
    setActiveFilters(emptyActiveFilters());
    setAppliedFilters(emptyAppliedFilters());
  }, [stageId]);

  // With no argument this copies the live selection into applied, which is what
  // triggers the refetch. The modal passes its own draft, because setActiveFilters
  // has not committed by the time its Done handler runs.
  const applyFilters = (nextActiveFilters?: ActiveHiringStageFilters) => {
    if (nextActiveFilters) {
      setActiveFilters(copyActiveFilters(nextActiveFilters));
      setAppliedFilters(toAppliedFilters(nextActiveFilters));
    } else {
      setAppliedFilters(toAppliedFilters(activeFilters));
    }
  };

  const clearFilters = () => {
    setActiveFilters(emptyActiveFilters());
    setAppliedFilters(emptyAppliedFilters());
  };

  return {
    activeFilters,
    appliedFilters,
    setActiveFilters,
    applyFilters,
    clearFilters,
  };
};
